import { queryOptions, useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { AppContext } from '../appContext';
import { getLoggedUser } from '../services/userService';
import { ProjectUserRoles, queryKeys } from '../utils/constants';
import useIsAuthenticated from './useIsAuthenticated';
import { getProjectRoles } from '../services/membershipService';

export default function useIsUserOnboard() {
  const { setUserMeta } = useContext(AppContext);
  const isAuthenticated = useIsAuthenticated();

  const query = useQuery(onboardingQueryOptions({ enabled: isAuthenticated }));

  useEffect(() => {
    const data = query.data;
    if (!data) return;

    const currentOrgRole = data.userData.organizations.find(
      (org) => org.organizationId === data.userData.organizationId,
    )?.role;

    const mappedProjectRoles = data.projectRolesData?.roles.reduce(
      (acc, curr) => {
        acc[curr.roleName as ProjectUserRoles] = curr.roleId;
        return acc;
      },
      {} as Record<ProjectUserRoles, string>,
    );

    setUserMeta({
      ...data.userData,
      projectRoleIds: mappedProjectRoles,
      organizationRole: currentOrgRole,
    });
  }, [query.data, setUserMeta]);

  return query;
}

export const onboardingQueryOptions = ({ enabled = true }: { enabled?: boolean } = {}) =>
  queryOptions({
    queryKey: [queryKeys.IS_USER_ON_BOARD],
    queryFn: async () => {
      const { data } = await getLoggedUser();
      const projectRoles = await getProjectRoles();

      const hasOrganization = !!data?.organizationId;
      const currentOrg = data.organizations.find(
        (org) => org.organizationId === data.organizationId,
      );
      const hasActiveOrganization = !!currentOrg?.isEnabled;

      const isOnboarded = hasOrganization && hasActiveOrganization;

      return {
        isOnboarded,
        hasOrganization,
        hasActiveOrganization,
        userData: data,
        projectRolesData: projectRoles.data,
      };
    },
  });
